import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Pagination, PaginationRequest } from '../models/pagination.model';
import { Package, PackageConfig, PackageStatus } from '../models/package.model';
import { PackageType } from '../models/package-type.model';
import { Policy } from '../models/policy.model';
import { BehaviorSubject, catchError, EMPTY, expand, map, Observable, of, reduce, share } from 'rxjs';
import { environment } from 'src/environments/environment';
import * as utils from '../../shared/helpers/utils';
import { License } from '../models/license.model';

@Injectable({
  providedIn: 'root'
})
export class LicenseService {

  getPackageTypes$?: Observable<Pagination<PackageType>>;
  getPackageStatus$?: Observable<Pagination<PackageStatus>>;

  packageConfig: PackageConfig = new PackageConfig();

  public currentPackageConfigSubject: BehaviorSubject<PackageConfig> = new BehaviorSubject(this.packageConfig);
  public currentPackageConfig!: Observable<PackageConfig>;

  constructor(
    private httpClient: HttpClient
  ) { }

  public get currentPackageConfigValue(): PackageConfig {
    return this.currentPackageConfigSubject.value;
  }

  public setCurrentPackageConfig(_value: PackageConfig | null) {

    this.currentPackageConfigSubject.next(this.packageConfig)
  }
  public setCurrentPackageParamConfig(_param: string, _value: any) {
    this.currentPackageConfigSubject.value[_param] = _value;
    this.currentPackageConfigSubject.next(this.currentPackageConfigSubject.value);
  }

  indexPackages(pagination: PaginationRequest): Observable<Pagination<Package>> {
    return this.httpClient.get<Pagination<Package>>(environment.apiUrl + '/packages', { params: utils.cleanParams(pagination) });
  }

  showPackage(id: string): Observable<Package> {
    return this.httpClient.get<Package>(environment.apiUrl + '/packages/' + id);
  }

  insertPackage(_package: Package): Observable<Package[]> {
    let finalpackage: Package = utils.cleanParams(_package);
    finalpackage.testDays = _package.testDays;
    return this.httpClient.post<Package[]>(environment.apiUrl + '/packages', finalpackage);
  }

  updatePackage(_package: Package): Observable<Package> {
    return this.httpClient.patch<Package>(environment.apiUrl + '/packages/' + _package.id, { ...utils.cleanParams(_package), deviceId: _package.deviceId });
  }

  cancelUpgrade(packageId: number): Observable<Package> {
    return this.httpClient.patch<Package>(environment.apiUrl + '/packages/' + packageId, { cancelUpgrade: true });
  }

  destroyPackage(id: number): Observable<Package> {
    return this.httpClient.delete<Package>(environment.apiUrl + '/packages/' + id);
  }

  availablePackages(queryParams: any): Observable<Pagination<Package>> {
    return this.httpClient.get<Pagination<Package>>(environment.apiUrl + '/packages', { params: utils.cleanParams(queryParams) });
  }

  /**
   * Fetches every page of /packages and emits ONCE with all packages accumulated in `data`.
   * Pages server-side in chunks of `perPage` (default 500) to avoid the AWS Lambda payload
   */
  availableAllPackages(queryParams: any, perPage: number = 500): Observable<Pagination<Package>> {
    const fetchPage = (page: number): Observable<{ page: number; res: Pagination<Package> }> =>
      this.availablePackages({ ...queryParams, perPage, page }).pipe(map(res => ({ page, res })));
    return fetchPage(1).pipe(
      expand(({ page, res }) => page < res.lastPage && res.data.length ? fetchPage(page + 1) : EMPTY),
      reduce((acc, { res }) => ({ ...res, data: [...acc.data, ...res.data] }), { data: [] } as unknown as Pagination<Package>)
    );
  }

  indexPackageTypes(pagination: PaginationRequest): Observable<Pagination<PackageType>> {
    return this.httpClient.get<Pagination<PackageType>>(environment.apiUrl + '/packagetypes', { params: utils.cleanParams(pagination) });
  }

  showPackageType(id: number): Observable<PackageType> {
    return this.httpClient.get<PackageType>(environment.apiUrl + '/packagetypes/' + id);
  }

  insertPackageType(packagetype: PackageType): Observable<PackageType> {
    return this.httpClient.post<PackageType>(environment.apiUrl + '/packagetypes', utils.cleanParams(packagetype));
  }

  updatePackageType(packagetype: PackageType): Observable<PackageType> {
    return this.httpClient.patch<PackageType>(environment.apiUrl + '/packagetypes/' + packagetype.id, utils.cleanParams(packagetype));
  }

  destroyPackageType(id: number): Observable<PackageType> {
    return this.httpClient.delete<PackageType>(environment.apiUrl + '/packagetypes/' + id);
  }

  indexPolicies(pagination: PaginationRequest): Observable<Pagination<Policy>> {
    return this.httpClient.get<Pagination<Policy>>(environment.apiUrl + '/policies', { params: utils.cleanParams(pagination) });
  }

  showPolicy(entityId: number, teamId?: number): Observable<Policy> {
    return this.httpClient.get<Policy>(environment.apiUrl + '/policies/' + entityId, { params: utils.cleanParams({ teamId }) }).pipe(catchError(error => { return of(error); }));
  }

  insertPolicy(policy: Policy): Observable<Policy> {
    return this.httpClient.post<Policy>(environment.apiUrl + '/policies', utils.cleanParams(policy));
  }

  updatePolicy(policy: Policy): Observable<Policy> {
    return this.httpClient.patch<Policy>(environment.apiUrl + '/policies/' + policy.id, utils.cleanParams(policy));
  }

  destroyPolicy(id: number): Observable<Policy> {
    return this.httpClient.delete<Policy>(environment.apiUrl + '/policies/' + id);
  }

  getPackageTypes(): Observable<Pagination<PackageType>> {
    if (!this.getPackageTypes$) {
      this.getPackageTypes$ = this.httpClient.get<Pagination<PackageType>>(environment.apiUrl + '/packagetypes?perPage=-1').pipe(share());
    }
    return this.getPackageTypes$;
  }

  getPackageStatus(): Observable<Pagination<PackageStatus>> {
    if (!this.getPackageStatus$) {
      this.getPackageStatus$ = this.httpClient.get<Pagination<PackageStatus>>(environment.apiUrl + '/packagestatus?perPage=-1').pipe(share());
    }
    return this.getPackageStatus$;
  }

}
